import { computed, useHead, useRuntimeConfig, useState, toRaw } from "#imports";
import { defu } from "defu";
import { useInfo, useLogError } from "./useLog.mjs";
import useConsent from "./useConsent.mjs";
import { twitterStandardEvents } from "../consts/eventNames.mjs";
export default function(input) {
  const { twitter, disabled, debug, loadingStrategy } = useRuntimeConfig().public.multiTracker;
  const pixelType = "Twitter";
  const options = useState("twitterPixelOptions");
  if (!options.value) {
    const temp = defu(input, twitter);
    options.value = {
      ...temp,
      pixelLoaded: false,
      isEnabled: !twitter.disabled ? !disabled : false,
      userData: null,
      eventsQueue: []
    };
  }
  const { haveConsent } = useConsent();
  const pixelDisabled = computed(
    () => !haveConsent.value || !options.value.isEnabled || !options.value.pixelID
  );
  const setPixel = () => {
    if (process.browser) {
      (function(e, t, n, s, u, a) {
        e.twq || (s = e.twq = function() {
          s.exe ? s.exe.apply(s, arguments) : s.queue.push(arguments);
        }, s.version = twitter.version, s.queue = []);
      })(window, document);
    }
    useHead({
      script: [
        {
          hid: "twitterPixel",
          src: "https://static.ads-twitter.com/uwt.js",
          onload: () => scriptLoaded(),
          defer: loadingStrategy === "defer"
        }
      ]
    });
    const scriptLoaded = () => {
      if (!window.twq) {
        useLogError(
          `(${pixelType}) script was loaded but functions is not avaible in "window".`
        );
      } else {
        options.value.pixelLoaded = true;
        track();
      }
    };
  };
  const setPixelId = (newPixelID) => {
    options.value.pixelID = newPixelID;
    useInfo(`${pixelType} pixel id set to "${options.value.pixelID}"`);
    init();
  };
  const setUserData = (newUserData, initPixel = true) => {
    if (!newUserData)
      return;
    if (JSON.stringify(options.value.userData) !== JSON.stringify(newUserData)) {
      options.value.userData = newUserData;
      if (initPixel) {
        init();
      }
    }
  };
  const enable = () => {
    options.value.isEnabled = true;
    init();
    track();
  };
  const disable = () => {
    options.value.isEnabled = false;
  };
  const init = () => {
    if (pixelDisabled.value)
      return;
    if (!options.value.pixelLoaded)
      setPixel();
    query("config", options.value.pixelID);
  };
  const track = (eventName = null, params = null, eventID = null) => {
    if (pixelDisabled.value)
      return;
    if (!eventName)
      eventName = options.value.track;
    const metaData = { ...params };
    if (eventID)
      metaData.conversion_id = eventID;
    if (twitterStandardEvents.includes(eventName)) {
      query("event", eventName, { ...metaData });
    } else {
      query("event", "Custom", { ...metaData });
    }
  };
  const query = (cmd, option, params = null) => {
    if (pixelDisabled.value)
      return;
    useInfo(`(${pixelType}) Cmd:`, cmd, "Option:", option, "Params:", params);
    options.value.eventsQueue.push({
      cmd,
      option,
      params
    });
    send();
  };
  const send = () => {
    if (!options.value.pixelLoaded)
      return;
    while (options.value.eventsQueue.length) {
      const event = options.value.eventsQueue.shift();
      if (debug)
        useInfo(`(${pixelType}) Send event: `, toRaw(event));
      if (event) {
        if (event.params) {
          window.twq(event.cmd, event.option, toRaw(event.params));
        } else {
          window.twq(event.cmd, event.option);
        }
      }
    }
  };
  return {
    options: options.value,
    setPixel,
    setPixelId,
    setUserData,
    enable,
    disable,
    track,
    query,
    init
  };
}
